import { SimpleTenant } from '../models/simple-tenant.model';
import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { AdminSetupComponent } from '../../admin/admin-setup/admin-setup.component';

// Preserve existing destinations, permissions and counters in the footer panel.
@Component({
  selector: 'app-system-menu',
  standalone: false,
  templateUrl: './system-menu.component.html',
  styleUrls: ['./system-menu.component.scss']
})
export class SystemMenuComponent extends AdminSetupComponent {
  @Input() tenant: SimpleTenant;
  @Input() desktop = false;
  activeCategory: string | null = null;

  categoryLabel(category: string): string {
    const names: {[key: string]: [string, string]} = {
      MEMBERSHIP: ['PEOPLE', 'PEOPLE'], SERVICES: ['SERVICES_EVENTS', 'SERVICES_EVENTS'],
      CONTENT_MANAGEMENT: ['CONTENT', 'CONTENT'], SYSTEM_SETTINGS: ['SETTINGS', 'SETTINGS'],
      SUPER_ADMIN: ['PLATFORM_ADMIN', 'PLATFORM_ADMIN']
    };
    return names[category] ? this.globalService.Get(names[category][0]) : this.globalService.Get(category);
  }

  menuLabel(key: string): string {
    if (key === 'EVENTS') return this.globalService.Get('MANAGE_EVENTS');
    const labels: {[key: string]: string} = {
      GROUP_SETUP: 'MENU_GROUP_SETUP', GROUP_REQUESTS: 'MENU_GROUP_REQUESTS', POINT_SYSTEM: 'MENU_POINT_SYSTEM', DIRECTORY: 'MENU_DIRECTORY', BIRTHDAY: 'MENU_BIRTHDAY', NOTES: 'MENU_NOTES', SELECTIVE: 'MENU_SELECTIVE',
      COLLECTIVE: 'MENU_COLLECTIVE', CATEGORIES: 'MENU_CATEGORIES', POLICIES: 'MENU_POLICIES',
      CONFIGURE: 'MENU_CONFIGURE', TAGS: 'MENU_TAGS', LINKS: 'MENU_LINKS', PERMISSIONS: 'MENU_PERMISSIONS',
      DATA_IMPORT: 'MENU_DATA_IMPORT', DATA_EXPORT: 'MENU_DATA_EXPORT', PAYMENT_ADAPTERS: 'MENU_PAYMENT_ADAPTERS',
      CHURCH_SETUP: 'MENU_CHURCH_SETUP', FUND_TYPES: 'MENU_FUND_TYPES', SUBSCRIPTION: 'MENU_SUBSCRIPTION'
    };
    return labels[key] ? this.globalService.Get(labels[key]) : this.globalService.Get(key);
  }

  settingsGroupLabel(group: string): string {
    return this.globalService.translateText(group);
  }

  actionIcon(action: string): string {
    const icons: {[key: string]: string} = {
      CallViewAccount:'fa-address-book', CallMembership:'fa-user-plus', CallBirthday:'fa-birthday-cake',
      CallSelectiveCommunication:'fa-comment', CallCommunication:'fa-comments', CallNotes:'fa-sticky-note',
      CallCalendar:'fa-calendar-alt', CallMyRegistrations:'fa-ticket-alt', CallEventReport:'fa-calendar-check',
      CallAttendance:'fa-user-check', CallServiceSetup:'fa-calendar-plus', CallDepartment:'fa-sitemap',
      CallSuperGroups:'fa-object-group', CallGroups:'fa-users', CallMyGroups:'fa-user-friends',
      CallGroupRequests:'fa-user-plus', callPointSystem:'fa-star', CallFormBuilder:'fa-edit',
      CallFormData:'fa-clipboard-check', CallMyForms:'fa-file-alt', CallFollowup:'fa-tasks',
      CallMyTasks:'fa-clipboard-list', CallNotifications:'fa-bell', CallVolunteering:'fa-hands-helping',
      CallMapDistribution:'fa-map-marked-alt', CallActivityChart:'fa-chart-line', CallDonationReport:'fa-chart-bar',
      CallDemographicsChart:'fa-chart-pie', CallCMS:'fa-layer-group', CallServiceGroup:'fa-church',
      CallServiceVenue:'fa-map-marker-alt', CallRegistrationGroup:'fa-clipboard-check', CallTags:'fa-tags',
      CallLinks:'fa-link', CallMemberPermission:'fa-key', CallImport:'fa-file-import',
      CallDataDownload:'fa-file-export', CallFundTypes:'fa-donate', CallPaymentAdapters:'fa-credit-card',
      CallShowSubscription:'fa-receipt', CallMyChurch:'fa-church', CallChurches:'fa-globe'
    };
    return icons[action] || 'fa-calendar-alt';
  }

  desktopCategory: string | null = null;
  desktopChild: string | null = null;
  flyouts: Record<string,{left:number;top:number;width:number;side:'left'|'right'}> = {};
  private triggers = new Map<string,HTMLElement>();
  private submenuTimer?: ReturnType<typeof setTimeout>;
  private leaveTimer?: ReturnType<typeof setTimeout>;
  private categoryCloseTimers = new Map<string,ReturnType<typeof setTimeout>>();
  isCategoryOpen(category:string){return this.desktop ? this.desktopCategory===category || this.desktopChild===category : this.activeCategory===category;}
  hoverCategory(category:string,event:PointerEvent){
    if(!this.desktop || event.pointerType!=='mouse')return;
    this.keepCategory(category);clearTimeout(this.submenuTimer);
    const trigger=event.currentTarget as HTMLElement;
    this.submenuTimer=setTimeout(()=>this.showFlyout(category,trigger),100);
  }
  keepCategory(category:string){
    clearTimeout(this.categoryCloseTimers.get(category));
    if(category==='COMMUNICATION' && this.desktopCategory)clearTimeout(this.categoryCloseTimers.get(this.desktopCategory));
  }
  leaveCategory(category:string){
    clearTimeout(this.submenuTimer);clearTimeout(this.categoryCloseTimers.get(category));
    if(!this.desktop)return;
    this.categoryCloseTimers.set(category,setTimeout(()=>{
      const panel=document.querySelector('[data-system-category="'+category+'"]');
      if(panel?.contains(document.activeElement))return;
      if(this.desktopChild===category)this.desktopChild=null;
      else if(this.desktopCategory===category){this.desktopCategory=null;this.desktopChild=null;}
    },170));
  }
  openCategory(category:string,event?:Event,focus=false):void {
    if(!this.desktop){this.activeCategory=category;return;}
    clearTimeout(this.submenuTimer);
    this.showFlyout(category,event?.currentTarget as HTMLElement,focus || (event?.type==='click' && (event as MouseEvent).detail===0));
  }
  private showFlyout(category:string,trigger:HTMLElement,focus=false){
    if(!trigger)return;
    this.keepCategory(category);
    this.triggers.set(category,trigger);
    if(category==='COMMUNICATION')this.desktopChild=category;
    else{this.desktopCategory=category;this.desktopChild=null;}
    const parent=trigger.closest<HTMLElement>('.system-flyout') || trigger.closest<HTMLElement>('.navigation-sheet') || trigger;
    const rect=parent.getBoundingClientRect(),row=trigger.getBoundingClientRect(),width=Math.min(300,window.innerWidth-24);
    const parentCategory=parent.dataset.systemCategory;
    const grid=trigger.closest<HTMLElement>('.system-category-grid')?.getBoundingClientRect() || rect;
    // Open outward from the physical column, regardless of reading direction.
    let side:'left'|'right'=parentCategory ? this.flyouts[parentCategory]?.side || 'right' : (row.left+row.right)/2 < (grid.left+grid.right)/2 ? 'left' : 'right';
    // Root flyouts touch their trigger's column, not the far edge of the
    // two-column sheet. An edge fallback can cover the other column so users
    // never have to cross a competing category to reach this submenu.
    const anchor=parentCategory ? rect : row;
    let left=side==='left' ? anchor.left-width-7 : anchor.right+7;
    if(left<12 || left+width>window.innerWidth-12){side=side==='left'?'right':'left';left=side==='left'?anchor.left-width-7:anchor.right+7;}
    this.flyouts[category]={left:Math.max(12,Math.min(left,window.innerWidth-width-12)),top:Math.max(12,Math.min(row.top,window.innerHeight-120)),width,side};
    setTimeout(()=>{
      if(!this.isCategoryOpen(category))return;
      const panel=document.querySelector<HTMLElement>('[data-system-category="'+category+'"]');
      if(!panel)return;
      const position=this.flyouts[category];
      position.top=Math.max(12,Math.min(position.top,window.innerHeight-panel.getBoundingClientRect().height-12));
      if(focus)panel.querySelector<HTMLButtonElement>('button')?.focus();
    },0);
  }
  @HostListener('pointerenter') keepFlyouts(){clearTimeout(this.leaveTimer);}
  @HostListener('pointerleave') leaveFlyouts(){
    clearTimeout(this.submenuTimer);
    this.leaveTimer=setTimeout(()=>{if(!Array.from(this.triggers.values()).some(el=>el.closest('.system-menu')?.contains(document.activeElement))){this.desktopCategory=null;this.desktopChild=null;}},170);
  }
  @HostListener('keydown',['$event']) flyoutKeys(event:KeyboardEvent){
    if(!this.desktop)return;
    const target=event.target as HTMLElement,category=target.closest<HTMLElement>('[data-system-trigger]')?.dataset.systemTrigger;
    const forward=this.globalService.isRTL?'ArrowLeft':'ArrowRight',back=this.globalService.isRTL?'ArrowRight':'ArrowLeft';
    if(event.key===forward && category){event.preventDefault();event.stopPropagation();this.openCategory(category,{currentTarget:target,type:'keydown'} as unknown as Event,true);}
    else if((event.key==='Escape'||event.key===back) && this.desktopCategory){
      event.preventDefault();event.stopPropagation();const closing=this.desktopChild || this.desktopCategory;
      if(this.desktopChild)this.desktopChild=null;else this.desktopCategory=null;
      this.triggers.get(closing)?.focus();
    }
  }
  ngOnDestroy(){clearTimeout(this.submenuTimer);clearTimeout(this.leaveTimer);for(const timer of this.categoryCloseTimers.values())clearTimeout(timer);}

  backToCategories(): void { this.activeCategory = this.activeCategory === 'COMMUNICATION' ? 'MEMBERSHIP' : null; }

  @Output() destinationSelected = new EventEmitter<void>();

  override async ngOnInit(): Promise<void> {
    this.isLoading = false;
    this.requests = 0;
    this.grpRequests = 0;

    this.cmsModule = this.tenant?.cmsModule === true;
    this.donationLink = this.tenant?.donationLink || null;
    this.churchWebsite = this.tenant?.churchWebsite || null;
    void this.loadFooterCounters();
  }

  // Opening navigation must preserve the current page title and state.
  override ngAfterViewInit(): void {}

  @HostListener('click', ['$event']) onDestinationClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('button.system-destination:not([data-submenu]), button[data-destination]')) {
      this.destinationSelected.emit();
    }
  }
}
